export interface Authorization {
  getBasicAuthorization(): string;
}
export enum HttpMethod {
  get = "GET",
  post = "POST",
  put = "PUT",
  patch = "PATCH",
  delete = "DELETE",
}
export enum ResponseType {
  json = "json",
  blob = "blob",
  arrayBuffer = "arraybuffer",
  text = "text",
}
export class HandlerParameters {
  readonly method: HttpMethod;
  readonly url: string;
  readonly header: Record<string, any>;
  readonly authorization?: Authorization;
  readonly body: Record<string, any>;
  readonly urlParams: Record<string, any>;
  readonly responseType: ResponseType;
  readonly cacheTtlMs?: number;
  constructor(builder: HandlerBuilder) {
    this.method = builder._method;
    this.url = builder._url;
    this.header = builder._header;
    this.authorization = builder._authorization;
    this.body = builder._body;
    this.urlParams = builder._urlParams;
    this.responseType = builder._responseType;
    this.cacheTtlMs = builder._cacheTtlMs;
  }
}
/**
 * Builder for BaseHandler Parameters
 * Default values are those needed in order to make a JSON request
 */
export class HandlerBuilder {
  CONTENT_TYPE: string;
  _method: HttpMethod;
  _url: string;
  _header: Record<string, any>;
  _authorization?: Authorization;
  _authorizationKey: string;
  _body: Record<string, any>;
  _urlParams: Record<string, any>;
  _responseType: ResponseType;
  _cacheTtlMs?: number;
  constructor() {
    this.CONTENT_TYPE = "application/json";
    this._method = HttpMethod.get;
    this._url = "";
    this._header = {};
    this._authorizationKey = "Authorization";
    this._body = {};
    this._urlParams = {};
    this._responseType = ResponseType.json;
    this.addHeader("Content-type", this.CONTENT_TYPE);
  }
  build(): HandlerParameters {
    return new HandlerParameters(this);
  }
  setMethod(value: HttpMethod): HandlerBuilder {
    this._method = value;
    return this;
  }
  setUrl(value: string): HandlerBuilder {
    this._url = value;
    return this;
  }
  setResponseType(value: ResponseType): HandlerBuilder {
    this._responseType = value;
    return this;
  }
  setAuthorization(value: Authorization, valueKey?: string): HandlerBuilder {
    this._authorization = value;
    if (valueKey) this._authorizationKey = valueKey;
    this._header[this._authorizationKey] =
      this._authorization.getBasicAuthorization();
    return this;
  }
  setHeaders(value: Record<string, any>): HandlerBuilder {
    value["Content-type"] = this.CONTENT_TYPE;
    value[this._authorizationKey] = this._authorization;
    this._header = value;
    return this;
  }
  addHeader(key: string, value: any): HandlerBuilder {
    this._header[key] = value;
    return this;
  }
  setBody(value: Record<string, any>): HandlerBuilder {
    this._body = value;
    return this;
  }
  addBody(key: string, value: any): HandlerBuilder {
    this._body[key] = value;
    return this;
  }
  setUrlParams(urlParams: Record<string, any>): HandlerBuilder {
    this._urlParams = urlParams;
    return this;
  }
  addUrlParam(key: string, value: any): HandlerBuilder {
    this._urlParams[key] = value;
    return this;
  }
  /**
   * Caches the response of a GET request for the given time.
   * Any POST/PUT/PATCH/DELETE made by the app clears the whole cache.
   */
  setCacheTtl(ms: number): HandlerBuilder {
    this._cacheTtlMs = ms;
    return this;
  }
  removeContentType(): HandlerBuilder {
    delete this._header["Content-type"];
    return this;
  }
}
